import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactForm from '../components/ContactForm';
import CTABanner from '../components/CTABanner';
import { Factory, CheckCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const roles = [
  'Production Line Workers',
  'Food Processing Operators',
  'Forklift Operators (Certified)',
  'Quality Control Inspectors',
  'Cleaning & Sanitation Crews',
  'Packaging & Labelling Staff',
  'Machine Operators',
  'Drivers & Delivery Personnel',
  'Inventory & Stock Workers',
  'Shift Supervisors',
];

const benefits = [
  'Workers trained in food safety and HACCP protocols',
  'Certified forklift operators on demand',
  'Cleaning and sanitation crews available for all shifts',
  'Licensed drivers for distribution runs',
  'Full WCB coverage and compliance',
  'Scalable workforce for seasonal demand',
  'Fast placement — workers in 24-48 hours',
  'Pre-screened for reliability and food handling',
];

const FoodProcessingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Food Processing Staffing BC - WorkNation Labour Ltd | Production Workers, Forklift Operators</title>
        <meta name="description" content="WorkNation Labour Ltd provides food processing staffing across BC. Production workers, forklift operators, cleaning & sanitation crews, drivers. HACCP trained, food safe certified. Contact us for fast placement." />
        <link rel="canonical" href="https://worknation.ca/industries/food-processing" />
      </Helmet>
      <Header />
      <main>
        <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-rose-50 via-white to-orange-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center mb-4">
              <Factory className="text-rose-600 mr-3" size={36} aria-hidden="true" />
              <span className="text-rose-600 font-semibold text-lg uppercase tracking-wide">Industry Spotlight</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#003366] mb-6">Food Processing Staffing in British Columbia</h1>
            <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mb-8">
              WorkNation Labour Ltd provides experienced food processing workers to production facilities, packaging plants, and food distribution operations across British Columbia. From production line workers and forklift operators to cleaning crews and drivers — we supply the workforce your food processing operation needs to run efficiently and safely.
            </p>
            <Button onClick={() => navigate('/employers')} className="bg-[#003366] hover:bg-[#004488] text-white px-8 py-6 text-lg" data-testid="food-processing-cta">
              Get Food Processing Workers Now <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </section>

        <section className="py-16 px-6 bg-white">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-6">Food Processing Roles We Fill</h2>
              <ul className="space-y-3">
                {roles.map((role, i) => (
                  <li key={i} className="flex items-center text-gray-700">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={20} aria-hidden="true" />
                    {role}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-6">Why Employers Choose Us</h2>
              <ul className="space-y-3">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start text-gray-700">
                    <CheckCircle className="text-[#003366] mr-3 flex-shrink-0 mt-0.5" size={20} aria-hidden="true" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-[#003366] mb-6">Food Processing Staffing Across BC</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              British Columbia&apos;s food processing industry is a critical part of the province&apos;s supply chain, from meat and poultry processing to dairy, bakery, beverage, and produce packaging. These operations require workers who understand food safety regulations, can operate in fast-paced production environments, and maintain strict hygiene and sanitation standards.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              WorkNation Labour Ltd supplies production workers, forklift operators, cleaning and sanitation crews, and drivers to food processing facilities across Metro Vancouver, Surrey, Richmond, Delta, and the Fraser Valley. All workers are pre-screened, food safety trained, and ready to work from day one.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Whether you need a small crew for a short production run or dozens of workers for peak season, WorkNation scales your workforce up or down based on demand. We handle all payroll, WCB, and administrative requirements so you can focus on production.
            </p>
          </div>
        </section>

        <section className="py-16 px-6 bg-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#003366] mb-6 text-center">Request Food Processing Workers</h2>
            <ContactForm type="employer" />
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </div>
  );
};

export default FoodProcessingPage;
